import type { BookingView, CurrentSession, CustomerList, MaintenanceList } from '@elite-residence/api-client';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, Fragment, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { loadMaintenance } from '../api/loadOperationalLists';
import { BookingContractPanel } from '../contract/ContractPages';
import { loadCustomers } from '../customer/loadCustomers';
import { businessDate } from '../dates';
import { BookingFinancePanel } from '../finance/FinancePages';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { Modal, PageHead } from '../ui/design';
import { Icon, type IconName } from '../ui/icons';
import { Alert, Badge, Field, Loading, QueryFailure, readResponse, useAction, useReadAction } from '../ui/ui';
import { BookingDocuments } from './BookingDocuments';
import { availabilityTone, groupByFloor } from './board';
import { historySummary, historyTitle, riyadhDateTime } from './history';
import { loadBookings } from './loadBookings';
import { MaintenanceDocuments } from './MaintenanceDocuments';
import { readinessKeys } from './readiness';

// Bookings, availability and Maintenance (V1-4-*). Money is shown and
// entered as exact strings; dates are business dates. Every action is
// offered only to holders of its key; the server rechecks everything.

type Booking = BookingView['booking'];
type Maintenance = MaintenanceList['maintenance'][number];

const text: Dictionary<
  | 'bookings'
  | 'availability'
  | 'branch'
  | 'start'
  | 'end'
  | 'search'
  | 'unit'
  | 'free'
  | 'blocked'
  | 'reserved'
  | 'filter'
  | 'anyStatus'
  | 'referenceSearch'
  | 'book'
  | 'newDraft'
  | 'customer'
  | 'occupant'
  | 'occupantsFailed'
  | 'customerSearch'
  | 'find'
  | 'total'
  | 'guests'
  | 'deposit'
  | 'checkin'
  | 'create'
  | 'reference'
  | 'status'
  | 'dates'
  | 'noBookings'
  | 'reserve'
  | 'gaps'
  | 'gap_guests'
  | 'gap_total_not_positive'
  | 'gap_company_occupant'
  | 'upcoming'
  | 'departureToday'
  | 'overstay'
  | 'negotiationWindow'
  | 'unresolvedAfterEnd'
  | 'changeDates'
  | 'changeTerms'
  | 'move'
  | 'toUnit'
  | 'extend'
  | 'newEnd'
  | 'added'
  | 'price'
  | 'newTotal'
  | 'cancel'
  | 'noShow'
  | 'checkIn'
  | 'checkOut'
  | 'earlyExit'
  | 'stayOpen'
  | 'stayClosed'
  | 'contractRequired'
  | 'rentRequired'
  | 'occupied'
  | 'exitBeforeEnd'
  | 'exitNotEarly'
  | 'archive'
  | 'restore'
  | 'archived'
  | 'archiveRent'
  | 'archiveDeposit'
  | 'archivePending'
  | 'reason'
  | 'reasonOptional'
  | 'apply'
  | 'history'
  | 'unavailable'
  | 'availabilityFailed'
  | 'physicalOccupancy'
  | 'customerFile'
  | 'effectiveTotal'
  | 'initialTotal'
  | 'maintenance'
  | 'newMaintenance'
  | 'expectedEnd'
  | 'notes'
  | 'close'
  | 'readiness'
  | 'cancelPlanned'
  | 'planning'
  | 'phase'
  | 'noMaintenance'
  | 'back'
  | 'missing'
  | 'checkInGuidance'
  | 'stale'
  | 'phase_planned'
  | 'phase_open'
  | 'phase_overdue'
  | 'phase_closed'
  | 'phase_cancelled'
  | 'planningHistory'
  | 'unitConflict'
  | 'savePlanning'
  | 'overview'
  | 'actions'
  | 'paymentsTab'
  | 'documentsTab'
  | 'contractTab'
  | 'sectionsNav'
  | 'editDetails'
  | 'backToBookings'
  | 'stayTitle'
  | 'nights'
  | 'previousPage'
  | 'nextPage'
  | 'riyadhTime'
  | 'when'
  | 'event'
  | 'details'
  | 'alreadyCheckedIn'
  | 'beforeStart'
  | 'inFuture'
  | 'statusNotAllowed'
  | 'recentActivity'
  | 'viewHistory'
  | 'createdAt'
  | 'none'
  | 'recordedKept'
> = {
  en: {
    bookings: 'Bookings',
    availability: 'Availability',
    branch: 'Branch',
    start: 'Start date',
    end: 'End date (departure)',
    search: 'Search',
    unit: 'Unit',
    free: 'Available',
    blocked: 'Blocked',
    reserved: 'Reserved',
    filter: 'Filter',
    anyStatus: 'Any status',
    referenceSearch: 'Reference',
    book: 'Book',
    newDraft: 'New Draft',
    customer: 'Customer',
    occupant: 'Company Occupant',
    occupantsFailed: 'The Company Occupants could not be loaded.',
    customerSearch: 'Customer name, reference, phone or email',
    find: 'Find',
    total: 'Agreed total (SAR, VAT included)',
    guests: 'Guests',
    deposit: 'Agreed Deposit (SAR, optional)',
    checkin: 'Scheduled check-in time (optional)',
    create: 'Create Draft',
    reference: 'Reference',
    status: 'Status',
    dates: 'Dates',
    noBookings: 'No Bookings.',
    reserve: 'Reserve',
    gaps: 'Before Reserve',
    gap_guests: 'Guests are missing.',
    gap_total_not_positive: 'The agreed total must be greater than zero.',
    gap_company_occupant: 'A Company Booking needs an Occupant.',
    upcoming: 'Upcoming',
    departureToday: 'Departure today',
    overstay: 'Overstay',
    negotiationWindow: 'Within three days after departure',
    unresolvedAfterEnd: 'Unsigned and past its end: resolve it',
    changeDates: 'Change dates',
    changeTerms: 'Correct unsigned Booking details',
    move: 'Unit Move',
    toUnit: 'Destination Unit',
    extend: 'Extension',
    newEnd: 'New end date',
    added: 'Added amount (SAR)',
    price: 'Price Change',
    newTotal: 'New total (SAR)',
    cancel: 'Cancel Booking',
    noShow: 'No-show',
    checkIn: 'Check in',
    checkOut: 'Check out',
    earlyExit: 'Early Exit',
    stayOpen: 'Checked in',
    stayClosed: 'Checked out',
    contractRequired: 'A signed contract is required before check-in.',
    rentRequired: 'At least one confirmed rent payment is required before check-in.',
    occupied: 'The Unit is physically occupied.',
    exitBeforeEnd: 'Checkout is available on the end date or later. Use Early Exit before then.',
    exitNotEarly: 'This date is not before the end date. Use Checkout.',
    archive: 'Archive',
    restore: 'Restore',
    archived: 'Archived',
    archiveRent: 'Rent is not settled, so this Booking cannot be archived.',
    archiveDeposit: 'The deposit is not settled, so this Booking cannot be archived.',
    archivePending: 'A pending financial transaction must be resolved before archive.',
    reason: 'Reason',
    reasonOptional: 'Reason (optional)',
    apply: 'Apply',
    history: 'History',
    unavailable: 'The Unit is not available for these dates.',
    availabilityFailed: 'Availability could not be loaded.',
    physicalOccupancy: 'Physically occupied now',
    customerFile: 'Customer file (through this Booking)',
    effectiveTotal: 'Current agreed total',
    initialTotal: 'Initial total',
    maintenance: 'Maintenance',
    newMaintenance: 'New blocking Maintenance',
    expectedEnd: 'Expected end (planning only)',
    notes: 'Notes',
    close: 'Close (Unit ready)',
    readiness: 'I confirm the Unit is physically ready.',
    cancelPlanned: 'Cancel planned Maintenance',
    planning: 'Change expected end',
    phase: 'Phase',
    noMaintenance: 'No Maintenance.',
    back: 'Back',
    missing: 'Fill in every required field.',
    checkInGuidance:
      'Check-in is offered when the booking is Active, the contract is signed, and one rent payment is confirmed. A remaining balance does not block checkout.',
    stale: 'This booking was changed. It has been reloaded; review it and try again.',
    phase_planned: 'Planned',
    phase_open: 'In progress',
    phase_overdue: 'Overdue',
    phase_closed: 'Closed',
    phase_cancelled: 'Cancelled',
    planningHistory: 'Planning history',
    unitConflict: 'This Unit already has a Booking or Maintenance covering the start date.',
    savePlanning: 'Save expected end',
    overview: 'Overview',
    actions: 'Actions',
    paymentsTab: 'Payments',
    documentsTab: 'Documents',
    contractTab: 'Contract',
    sectionsNav: 'Booking sections',
    editDetails: 'Edit details',
    backToBookings: 'Back to bookings',
    stayTitle: 'Stay',
    nights: 'nights',
    previousPage: 'Previous',
    nextPage: 'Next',
    riyadhTime: 'Riyadh time',
    when: 'When (Riyadh)',
    event: 'Event',
    details: 'Details',
    alreadyCheckedIn: 'This Booking is already checked in.',
    beforeStart: 'This is before the Booking start date (Riyadh business date).',
    inFuture: 'The time cannot be in the future.',
    statusNotAllowed: 'The Booking status does not allow this action. Refresh and try again.',
    recentActivity: 'Recent activity',
    viewHistory: 'View history',
    createdAt: 'Created',
    none: 'None yet.',
    recordedKept: 'Recorded transactions are kept. Money is returned with a separate refund transaction.',
  },
  ar: {
    bookings: 'الحجوزات',
    availability: 'التوفر',
    branch: 'الفرع',
    start: 'تاريخ البداية',
    end: 'تاريخ النهاية (المغادرة)',
    search: 'بحث',
    unit: 'الوحدة',
    free: 'متاحة',
    blocked: 'محجوزة',
    reserved: 'محجوزة',
    filter: 'تصفية',
    anyStatus: 'كل الحالات',
    referenceSearch: 'المرجع',
    book: 'حجز',
    newDraft: 'مسودة جديدة',
    customer: 'العميل',
    occupant: 'شاغل الشركة',
    occupantsFailed: 'تعذر تحميل شاغلي الشركة.',
    customerSearch: 'اسم العميل أو المرجع أو الهاتف أو البريد',
    find: 'بحث',
    total: 'الإجمالي المتفق عليه (ريال، شامل الضريبة)',
    guests: 'عدد الضيوف',
    deposit: 'التأمين المتفق عليه (ريال، اختياري)',
    checkin: 'وقت الدخول المجدول (اختياري)',
    create: 'إنشاء مسودة',
    reference: 'المرجع',
    status: 'الحالة',
    dates: 'التواريخ',
    noBookings: 'لا توجد حجوزات.',
    reserve: 'تأكيد الحجز',
    gaps: 'قبل تأكيد الحجز',
    gap_guests: 'عدد الضيوف غير مدخل.',
    gap_total_not_positive: 'يجب أن يكون الإجمالي أكبر من صفر.',
    gap_company_occupant: 'حجز الشركة يحتاج إلى شاغل.',
    upcoming: 'قادم',
    departureToday: 'مغادرة اليوم',
    overstay: 'تجاوز المدة',
    negotiationWindow: 'خلال ثلاثة أيام بعد المغادرة',
    unresolvedAfterEnd: 'غير موقع وتجاوز نهايته: يجب حسمه',
    changeDates: 'تغيير التواريخ',
    changeTerms: 'تصحيح تفاصيل الحجز غير الموقّع',
    move: 'نقل الوحدة',
    toUnit: 'الوحدة الجديدة',
    extend: 'تمديد',
    newEnd: 'تاريخ النهاية الجديد',
    added: 'المبلغ المضاف (ريال)',
    price: 'تغيير السعر',
    newTotal: 'الإجمالي الجديد (ريال)',
    cancel: 'إلغاء الحجز',
    noShow: 'عدم الحضور',
    checkIn: 'تسجيل الدخول',
    checkOut: 'تسجيل الخروج',
    earlyExit: 'خروج مبكر',
    stayOpen: 'تم تسجيل الدخول',
    stayClosed: 'تم تسجيل الخروج',
    contractRequired: 'يلزم عقد موقّع قبل تسجيل الدخول.',
    rentRequired: 'يلزم دفعة إيجار مؤكدة واحدة على الأقل قبل تسجيل الدخول.',
    occupied: 'الوحدة مشغولة فعليًا.',
    exitBeforeEnd: 'تسجيل الخروج متاح في تاريخ النهاية أو بعده. استخدم الخروج المبكر قبل ذلك.',
    exitNotEarly: 'هذا التاريخ ليس قبل تاريخ النهاية. استخدم تسجيل الخروج.',
    archive: 'أرشفة',
    restore: 'استعادة',
    archived: 'مؤرشف',
    archiveRent: 'حساب الإيجار غير مسوّى، لذلك لا يمكن أرشفة هذا الحجز.',
    archiveDeposit: 'التأمين غير مسوّى، لذلك لا يمكن أرشفة هذا الحجز.',
    archivePending: 'يجب حسم الحركة المالية المعلقة قبل الأرشفة.',
    reason: 'السبب',
    reasonOptional: 'السبب (اختياري)',
    apply: 'تطبيق',
    history: 'السجل',
    unavailable: 'الوحدة غير متاحة لهذه التواريخ.',
    availabilityFailed: 'تعذر تحميل التوفر.',
    physicalOccupancy: 'مشغولة فعلياً الآن',
    customerFile: 'ملف العميل (من خلال هذا الحجز)',
    effectiveTotal: 'الإجمالي الحالي',
    initialTotal: 'الإجمالي الأولي',
    maintenance: 'الصيانة',
    newMaintenance: 'صيانة مانعة جديدة',
    expectedEnd: 'النهاية المتوقعة (للتخطيط فقط)',
    notes: 'ملاحظات',
    close: 'إغلاق (الوحدة جاهزة)',
    readiness: 'أؤكد أن الوحدة جاهزة فعليًا.',
    cancelPlanned: 'إلغاء الصيانة المخططة',
    planning: 'تغيير النهاية المتوقعة',
    phase: 'المرحلة',
    noMaintenance: 'لا توجد صيانة.',
    back: 'رجوع',
    missing: 'أكمل جميع الحقول المطلوبة.',
    checkInGuidance:
      'يُتاح تسجيل الدخول عندما يكون الحجز نشطًا والعقد موقّعًا وتوجد دفعة إيجار مؤكدة واحدة. الرصيد المتبقي لا يمنع تسجيل الخروج.',
    stale: 'تغيّر هذا الحجز. أُعيد تحميله؛ راجعه ثم أعد المحاولة.',
    phase_planned: 'مخططة',
    phase_open: 'جارية',
    phase_overdue: 'متأخرة',
    phase_closed: 'مغلقة',
    phase_cancelled: 'ملغاة',
    planningHistory: 'سجل التخطيط',
    unitConflict: 'هذه الوحدة لديها حجز أو صيانة تغطي تاريخ البداية.',
    savePlanning: 'حفظ النهاية المتوقعة',
    overview: 'نظرة عامة',
    actions: 'الإجراءات',
    paymentsTab: 'المدفوعات',
    documentsTab: 'المستندات',
    contractTab: 'العقد',
    sectionsNav: 'أقسام الحجز',
    editDetails: 'تعديل التفاصيل',
    backToBookings: 'العودة إلى الحجوزات',
    stayTitle: 'الإقامة',
    nights: 'ليالٍ',
    previousPage: 'السابق',
    nextPage: 'التالي',
    riyadhTime: 'توقيت الرياض',
    when: 'الوقت (الرياض)',
    event: 'الحدث',
    details: 'التفاصيل',
    alreadyCheckedIn: 'تم تسجيل دخول هذا الحجز مسبقًا.',
    beforeStart: 'هذا قبل تاريخ بداية الحجز (تاريخ العمل بتوقيت الرياض).',
    inFuture: 'لا يمكن أن يكون الوقت في المستقبل.',
    statusNotAllowed: 'حالة الحجز لا تسمح بهذا الإجراء. حدّث الصفحة وحاول مرة أخرى.',
    recentActivity: 'آخر النشاط',
    viewHistory: 'عرض السجل',
    createdAt: 'أُنشئ',
    none: 'لا يوجد بعد.',
    recordedKept: 'العمليات المسجلة باقية. تُعاد المبالغ بعملية استرداد مستقلة.',
  },
};

const has = (session: CurrentSession, key: string) => session.permissions.includes(key);
const LIST_PAGE = 20;
export const STATUS_LABELS: Record<string, { en: string; ar: string }> = {
  DRAFT: { en: 'Draft', ar: 'مسودة' },
  RESERVED: { en: 'Reserved', ar: 'محجوز' },
  ACTIVE: { en: 'Active', ar: 'نشط' },
  COMPLETED: { en: 'Completed', ar: 'مكتمل' },
  CANCELLED: { en: 'Cancelled', ar: 'ملغى' },
  ENDED_EARLY: { en: 'Ended early', ar: 'منتهٍ مبكرًا' },
  NO_SHOW: { en: 'No-show', ar: 'عدم حضور' },
};

function useBranches() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['property', 'branches', 'active'],
    queryFn: async ({ signal }) => {
      const r = await client.listBranches({}, { signal });
      return readResponse(r).branches.filter((b) => !b.archived);
    },
  });
}

function StatusBadge({ status }: { readonly status: string }) {
  const { locale } = useLocale();
  const tone = status === 'RESERVED' || status === 'ACTIVE' ? 'ok' : status === 'DRAFT' ? 'warn' : 'off';
  return <Badge tone={tone}>{STATUS_LABELS[status]?.[locale] ?? status}</Badge>;
}

// ---- list, availability and Draft creation ---------------------------------------------------

export function BookingsPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const branches = useBranches();
  const today = businessDate(new Date());
  const [search, setSearch] = useState({ branchId: '', start: '', end: '' });
  const [picked, setPicked] = useState<{ unitId: string; unitNumber: string } | null>(null);
  const availability = useQuery({
    queryKey: ['availability', search],
    enabled: Boolean(search.branchId && search.start && search.end),
    queryFn: async ({ signal }) => {
      const r = await client.getUnitAvailability({ query: search }, { signal });
      if (r.status !== 200) throw new Error('availability_unavailable');
      return r.body.units;
    },
  });
  const floors = useQuery({
    queryKey: ['availability', 'floors', search.branchId],
    enabled: Boolean(search.branchId),
    queryFn: async ({ signal }) => {
      const r = await client.listFloors({ path: { branchId: search.branchId }, query: {} }, { signal });
      return readResponse(r).floors;
    },
  });
  const [form, setForm] = useState({ branchId: '', start: today, end: businessDate(new Date(), 1) });
  const [tableForm, setTableForm] = useState({ status: '', from: '', to: '', text: '' });
  const [tableQuery, setTableQuery] = useState({ status: '', from: '', to: '' });
  const bookings = useQuery({
    queryKey: ['bookings', tableQuery],
    queryFn: async ({ signal }) => {
      const query: { status?: string; from?: string; to?: string } = {};
      if (tableQuery.status) query.status = tableQuery.status;
      if (tableQuery.from) query.from = tableQuery.from;
      if (tableQuery.to) query.to = tableQuery.to;
      return loadBookings(client, query, signal);
    },
  });
  const shown = (bookings.data ?? []).filter((booking) =>
    booking.displayReference.toLowerCase().includes(tableForm.text.trim().toLowerCase()),
  );
  // Prototype list columns from existing reads: unit numbers (inventory
  // scope), branch names, and the basic name of the customers on the current
  // page only (customer.basic_view), so a long list never fans out reads.
  const [listPage, setListPage] = useState(0);
  const pages = Math.max(1, Math.ceil(shown.length / LIST_PAGE));
  const currentPage = Math.min(listPage, pages - 1);
  const pageRows = shown.slice(currentPage * LIST_PAGE, currentPage * LIST_PAGE + LIST_PAGE);
  const listUnits = useQuery({
    queryKey: ['search', 'units'],
    staleTime: 60_000,
    queryFn: async ({ signal }) => readResponse(await client.listUnits({ query: {} }, { signal })).units,
  });
  const unitOf = (unitId: string) => listUnits.data?.find((u) => u.id === unitId);
  const branchName = (branchId: string) => branches.data?.find((b) => b.id === branchId)?.name;
  const customerReads = useQueries({
    queries: has(session, 'customer.basic_view')
      ? [...new Set(pageRows.map((b) => b.customerId))].map((customerId) => ({
          queryKey: ['board', 'customer', customerId],
          staleTime: 60_000,
          queryFn: async ({ signal }: { signal: AbortSignal }) =>
            readResponse(await client.getCustomer({ path: { customerId } }, { signal })).customer,
        }))
      : [],
  });
  const customerName = (customerId: string) => customerReads.find((q) => q.data?.id === customerId)?.data?.name;
  const nights = (start: string, end: string) =>
    Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000);
  const groups = groupByFloor(
    availability.data ?? [],
    (floors.data ?? []).map((floor) => ({
      id: floor.id,
      label: floor.labels[locale],
    })),
  );
  return (
    <main>
      <PageHead
        title={t.bookings}
        actions={
          <nav className="er-seg" aria-label={t.bookings}>
            <Link className="er-seg__opt" to="/board">
              <Icon name="board" />
              {locale === 'ar' ? 'لوحة الوحدات' : 'Unit board'}
            </Link>
            <span className="er-seg__opt is-active" aria-current="page">
              <Icon name="list" />
              {t.bookings}
            </span>
          </nav>
        }
      />
      <section className="er-card">
        <h2>{t.availability}</h2>
        <QueryFailure query={branches} />
        <QueryFailure query={floors} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPicked(null);
            setSearch(form);
          }}
          noValidate
        >
          <Field id="av-branch" label={t.branch}>
            <select
              id="av-branch"
              value={form.branchId}
              onChange={(e) => setForm({ ...form, branchId: e.target.value })}
            >
              <option value="" />
              {(branches.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <Field id="av-start" label={t.start}>
            <input
              id="av-start"
              type="date"
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
            />
          </Field>
          <Field id="av-end" label={t.end}>
            <input
              id="av-end"
              type="date"
              value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })}
            />
          </Field>
          <button className="er-button" type="submit" disabled={!form.branchId || !form.start || !form.end}>
            {t.search}
          </button>
        </form>
        {availability.isError && <Alert kind="error">{t.availabilityFailed}</Alert>}
        {availability.isFetching ? (
          <Loading />
        ) : (
          availability.data && (
            <table className="er-table" aria-label={t.availability}>
              <tbody>
                {groups.map((group) => (
                  <Fragment key={group.floorId}>
                    <tr>
                      <th scope="rowgroup" colSpan={3}>
                        {group.label}
                      </th>
                    </tr>
                    {group.units.map((u) => {
                      const tone = availabilityTone(u);
                      return (
                        <tr key={u.unitId}>
                          <td className="er-mono">{u.unitNumber}</td>
                          <td>
                            <Badge tone={tone === 'available' ? 'ok' : tone === 'maintenance' ? 'danger' : 'warn'}>
                              {tone === 'available' ? t.free : tone === 'maintenance' ? t.maintenance : t.reserved}
                            </Badge>{' '}
                            {u.physicalOccupied && <Badge tone="warn">{t.physicalOccupancy}</Badge>}{' '}
                            {u.overstay && <Badge tone="danger">{t.overstay}</Badge>}{' '}
                            <span className="er-mono">
                              {u.conflicts.map((c) => `${c.start} → ${c.end ?? '…'}`).join(', ')}
                            </span>
                          </td>
                          <td>
                            {u.available && has(session, 'booking.create') && (
                              <button className="er-button secondary" type="button" onClick={() => setPicked(u)}>
                                {t.book} {u.unitNumber}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )
        )}
      </section>
      {picked && (
        <NewDraft unitId={picked.unitId} unitNumber={picked.unitNumber} start={search.start} end={search.end} />
      )}
      <section className="er-card">
        <h2>{t.bookings}</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setTableQuery({ status: tableForm.status, from: tableForm.from, to: tableForm.to });
          }}
          noValidate
        >
          <Field id="bk-status" label={t.status}>
            <select
              id="bk-status"
              value={tableForm.status}
              onChange={(event) => setTableForm({ ...tableForm, status: event.target.value })}
            >
              <option value="">{t.anyStatus}</option>
              {Object.keys(STATUS_LABELS).map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]?.[locale]}
                </option>
              ))}
            </select>
          </Field>
          <Field id="bk-from" label={t.start}>
            <input
              id="bk-from"
              type="date"
              value={tableForm.from}
              onChange={(event) => setTableForm({ ...tableForm, from: event.target.value })}
            />
          </Field>
          <Field id="bk-to" label={t.end}>
            <input
              id="bk-to"
              type="date"
              value={tableForm.to}
              onChange={(event) => setTableForm({ ...tableForm, to: event.target.value })}
            />
          </Field>
          <Field id="bk-text" label={t.referenceSearch}>
            <input
              id="bk-text"
              value={tableForm.text}
              onChange={(event) => setTableForm({ ...tableForm, text: event.target.value })}
            />
          </Field>
          <button className="er-button" type="submit">
            {t.filter}
          </button>
        </form>
        {bookings.isPending ? (
          <Loading />
        ) : bookings.isError ? (
          <Alert kind="error">{locale === 'ar' ? 'تعذر تحميل الحجوزات.' : 'Bookings could not be loaded.'}</Alert>
        ) : shown.length === 0 ? (
          <p>{t.noBookings}</p>
        ) : (
          <>
            <table className="er-table">
              <thead>
                <tr>
                  <th>{t.reference}</th>
                  <th>{t.customer}</th>
                  <th>{t.unit}</th>
                  <th>{t.status}</th>
                  <th>{t.dates}</th>
                  <th className="num">{t.effectiveTotal}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((b) => (
                  <tr key={b.id}>
                    <td>
                      <Link to={`/bookings/${b.id}`}>{b.displayReference}</Link>
                    </td>
                    <td>
                      <span className="er-cell-main">{customerName(b.customerId) ?? '—'}</span>
                    </td>
                    <td>
                      <span className="er-cell-main" dir="ltr">
                        {unitOf(b.unitId)?.unitNumber ?? '—'}
                      </span>
                      <span className="er-cell-sub" style={{ display: 'block' }}>
                        {branchName(b.branchId) ?? ''}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={b.status} />{' '}
                      {b.indicators.upcoming && <Badge tone="warn">{t.upcoming}</Badge>}{' '}
                      {b.indicators.departureToday && <Badge tone="warn">{t.departureToday}</Badge>}{' '}
                      {b.indicators.overstay && <Badge tone="danger">{t.overstay}</Badge>}{' '}
                      {b.indicators.unresolvedAfterEnd && <Badge tone="danger">{t.unresolvedAfterEnd}</Badge>}
                    </td>
                    <td>
                      <span className="er-mono">
                        {b.start} → {b.end}
                      </span>
                      <span className="er-cell-sub" style={{ display: 'block' }}>
                        {nights(b.start, b.end)} {t.nights}
                      </span>
                    </td>
                    <td className="er-mono num" dir="ltr">
                      {b.effectiveTotal}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pages > 1 ? (
              <div className="er-actionbar">
                <button
                  className="er-btn er-btn--secondary er-btn--sm"
                  type="button"
                  disabled={currentPage === 0}
                  onClick={() => setListPage(currentPage - 1)}
                >
                  {t.previousPage}
                </button>
                <span className="er-cell-sub">
                  {currentPage + 1} / {pages} · {shown.length}
                </span>
                <button
                  className="er-btn er-btn--secondary er-btn--sm"
                  type="button"
                  disabled={currentPage >= pages - 1}
                  onClick={() => setListPage(currentPage + 1)}
                >
                  {t.nextPage}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>
    </main>
  );
}

export function NewDraft({
  unitId,
  unitNumber,
  start,
  end,
}: {
  readonly unitId: string;
  readonly unitNumber: string;
  readonly start: string;
  readonly end: string;
}) {
  const client = useApiClient();
  const t = useText(text);
  const navigate = useNavigate();
  const action = useAction();
  const [query, setQuery] = useState('');
  const [customers, setCustomers] = useState<CustomerList['customers']>([]);
  const search = useReadAction();
  const [form, setForm] = useState({
    customerId: '',
    occupantId: '',
    total: '',
    guests: '1',
    deposit: '',
    checkin: '',
  });

  async function find(event: FormEvent) {
    event.preventDefault();
    setCustomers([]);
    const r = await search.run(() => loadCustomers(client, { text: query }));
    if (r?.status === 200) setCustomers(r.body.customers.filter((c) => !c.archived && !c.mergedIntoCustomerId));
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!form.customerId || !form.total) {
      action.setError(t.missing);
      return;
    }
    const r = await action.run((key) =>
      client.createDraftBooking({
        headers: { 'idempotency-key': key },
        body: {
          unitId,
          customerId: form.customerId,
          ...(form.occupantId ? { occupantId: form.occupantId } : {}),
          startDate: start,
          endDate: end,
          agreedTotal: form.total.trim(),
          ...(form.guests ? { guests: Number(form.guests) } : {}),
          ...(form.deposit.trim() ? { agreedDeposit: form.deposit.trim() } : {}),
          ...(form.checkin ? { scheduledCheckinTime: form.checkin } : {}),
        },
      }),
    );
    if (r && (r.status === 200 || r.status === 201)) navigate(`/bookings/${r.body.booking.id}`);
  }

  return (
    <section className="er-card">
      <h2>
        {t.newDraft} — {t.unit} {unitNumber} ({start} → {end})
      </h2>
      <form onSubmit={find} noValidate>
        <Field id="draft-customer-search" label={t.customerSearch}>
          <input id="draft-customer-search" value={query} onChange={(e) => setQuery(e.target.value)} />
        </Field>
        <button className="er-button secondary" type="submit" disabled={!query.trim() || search.busy}>
          {t.find}
        </button>
      </form>
      {search.error && <Alert kind="error">{search.error}</Alert>}
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <form onSubmit={create} noValidate>
        <Field id="draft-customer" label={t.customer}>
          <select
            id="draft-customer"
            value={form.customerId}
            onChange={(e) => setForm({ ...form, customerId: e.target.value, occupantId: '' })}
          >
            <option value="" />
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayReference} — {c.name}
              </option>
            ))}
          </select>
        </Field>
        {customers.find((customer) => customer.id === form.customerId)?.type === 'company' && (
          <CompanyOccupantPicker
            id="draft-occupant"
            customerId={form.customerId}
            value={form.occupantId}
            onChange={(occupantId) => setForm({ ...form, occupantId })}
          />
        )}
        <Field id="draft-total" label={t.total}>
          <input
            id="draft-total"
            dir="ltr"
            inputMode="decimal"
            value={form.total}
            onChange={(e) => setForm({ ...form, total: e.target.value })}
          />
        </Field>
        <Field id="draft-guests" label={t.guests}>
          <input
            id="draft-guests"
            type="number"
            min={1}
            value={form.guests}
            onChange={(e) => setForm({ ...form, guests: e.target.value })}
          />
        </Field>
        <Field id="draft-deposit" label={t.deposit}>
          <input
            id="draft-deposit"
            dir="ltr"
            inputMode="decimal"
            value={form.deposit}
            onChange={(e) => setForm({ ...form, deposit: e.target.value })}
          />
        </Field>
        <Field id="draft-checkin" label={t.checkin}>
          <input
            id="draft-checkin"
            type="time"
            value={form.checkin}
            onChange={(e) => setForm({ ...form, checkin: e.target.value })}
          />
        </Field>
        <button className="er-button" type="submit" disabled={action.busy}>
          {t.create}
        </button>
      </form>
    </section>
  );
}

// ---- detail -----------------------------------------------------------------------------------

function CompanyOccupantPicker({
  id,
  customerId,
  value,
  onChange,
}: {
  readonly id: string;
  readonly customerId: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  const client = useApiClient();
  const t = useText(text);
  const occupants = useQuery({
    queryKey: ['customer-occupants', customerId],
    queryFn: async ({ signal }) => {
      const response = await client.listCompanyOccupants({ path: { customerId } }, { signal });
      if (response.status !== 200) throw new Error('company_occupants_unavailable');
      return response.body.occupants;
    },
  });
  return (
    <>
      {occupants.isError && <Alert kind="error">{t.occupantsFailed}</Alert>}
      <Field id={id} label={t.occupant}>
        <select
          id={id}
          value={value}
          disabled={occupants.isPending || occupants.isError}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="" />
          {(occupants.data ?? []).map((occupant) => (
            <option key={occupant.id} value={occupant.id}>
              {occupant.name}
            </option>
          ))}
        </select>
      </Field>
    </>
  );
}

export function BookingDetailPage({ session }: { readonly session: CurrentSession }) {
  const { bookingId = '' } = useParams();
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const queries = useQueryClient();
  // The selected tab lives in the address, so a reload or a shared link
  // returns to the same section.
  const [searchParams, setSearchParams] = useSearchParams();
  const booking = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: async ({ signal }) => {
      const r = await client.getBooking({ path: { bookingId } }, { signal });
      return readResponse(r).booking;
    },
  });
  const history = useQuery({
    queryKey: ['booking', bookingId, 'history'],
    queryFn: async ({ signal }) => {
      const r = await client.getBookingHistory({ path: { bookingId } }, { signal });
      return readResponse(r).entries;
    },
  });
  // Unit numbers for readable history (inventory scope; unreadable stays a dash).
  const units = useQuery({
    queryKey: ['search', 'units'],
    staleTime: 60_000,
    queryFn: async ({ signal }) => readResponse(await client.listUnits({ query: {} }, { signal })).units,
  });
  const unitNumber = (unitId: string) => units.data?.find((u) => u.id === unitId)?.unitNumber;
  const refresh = () =>
    Promise.all([
      queries.invalidateQueries({ queryKey: ['booking', bookingId] }),
      queries.invalidateQueries({ queryKey: ['contract', bookingId] }),
    ]);
  if (booking.isPending) return <Loading />;
  if (booking.isError) return <QueryFailure query={booking} />;
  const b = booking.data;
  if (!b) {
    return (
      <main>
        <Alert kind="error">{t.noBookings}</Alert>
        <Link to="/bookings">{t.back}</Link>
      </main>
    );
  }
  const flags = (Object.keys(b.indicators) as (keyof Booking['indicators'])[]).filter((k) => b.indicators[k]);
  const tabs = [
    ['overview', t.overview],
    ['payments', t.paymentsTab],
    ['documents', t.documentsTab],
    ['contract', t.contractTab],
    ['history', t.history],
  ] as const;
  const requested = searchParams.get('tab');
  const tab = tabs.some(([key]) => key === requested) ? (requested as (typeof tabs)[number][0]) : 'overview';
  const entries = history.data ?? [];
  return (
    <main>
      <div className="er-crumbs">
        <Icon name="arrow" className="er-crumb-back" />
        <Link to="/bookings">{t.backToBookings}</Link>
      </div>
      <section className="er-panel er-section-block er-whead">
        <div className="er-whead__top">
          <span className="er-avatar" aria-hidden="true">
            <Icon name="calendar" />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1>
              <span className="er-mono">{b.displayReference}</span> <StatusBadge status={b.status} />{' '}
              {b.archived && <Badge tone="warn">{t.archived}</Badge>}
            </h1>
            <div className="er-pagesub">
              <Link to={`/customers/${b.customerId}?bookingId=${b.id}`}>{t.customerFile}</Link>
            </div>
          </div>
          <div className="er-whead__chips">
            {flags.map((f) => (
              <Badge key={f} tone="warn">
                {t[f]}
              </Badge>
            ))}
          </div>
        </div>
        <div className="er-strip">
          <div>
            <span className="er-overline">{t.dates}</span>
            <b className="er-strip__v er-mono">
              {b.start} → {b.end}
            </b>
          </div>
          <div>
            <span className="er-overline">{t.effectiveTotal}</span>
            <b className="er-strip__v" dir="ltr">
              {b.effectiveTotal}
            </b>
            <span className="er-cell-sub">SAR</span>
          </div>
          <div>
            <span className="er-overline">{t.initialTotal}</span>
            <b className="er-strip__v" dir="ltr">
              {b.initialTotal}
            </b>
          </div>
          <div>
            <span className="er-overline">{t.deposit}</span>
            <b className="er-strip__v" dir="ltr">
              {b.agreedDeposit ?? '—'}
            </b>
          </div>
          <div>
            <span className="er-overline">{t.guests}</span>
            <b className="er-strip__v">{b.guests ?? '—'}</b>
          </div>
        </div>
        {b.stay ||
        (b.status === 'DRAFT' && b.reserveGaps.length > 0) ||
        readinessKeys(b).includes('checkInGuidance') ? (
          <div className="er-panel__body">
            {b.stay && (
              <p>
                {b.stay.checkedOutAt ? t.stayClosed : t.stayOpen}:{' '}
                <span dir="ltr">{riyadhDateTime(locale, b.stay.checkedInAt)}</span>
                {b.stay.checkedOutAt && (
                  <>
                    {' → '}
                    <span dir="ltr">{riyadhDateTime(locale, b.stay.checkedOutAt)}</span>
                  </>
                )}{' '}
                <span className="er-cell-sub">({t.riyadhTime})</span>
              </p>
            )}
            {b.status === 'DRAFT' && b.reserveGaps.length > 0 && (
              <div className="er-callout">
                <Icon name="alert" />
                <div>
                  <h2 style={{ fontSize: 14, margin: 0 }}>{t.gaps}</h2>
                  <ul style={{ margin: 0 }}>
                    {readinessKeys(b).map((key) => (
                      <li key={key}>{t[key]}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {readinessKeys(b).includes('checkInGuidance') && <p className="er-hint">{t.checkInGuidance}</p>}
          </div>
        ) : null}
      </section>
      <BookingActions key={b.id} booking={b} session={session} onChanged={refresh} />
      <div className="er-tabs" role="tablist" aria-label={t.sectionsNav}>
        {tabs.map(([key, label]) => (
          <button
            key={key}
            id={`bk-tab-${key}`}
            type="button"
            role="tab"
            className="er-tab"
            aria-selected={tab === key}
            aria-controls={`bk-panel-${key}`}
            tabIndex={tab === key ? 0 : -1}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (key === 'overview') next.delete('tab');
              else next.set('tab', key);
              setSearchParams(next, { replace: true });
            }}
          >
            {label}
            {key === 'history' && history.data ? (
              <span className="er-tab__count" aria-hidden="true">
                {entries.length}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div id={`bk-panel-${tab}`} role="tabpanel" aria-labelledby={`bk-tab-${tab}`}>
        {tab === 'overview' && (
          <div className="er-grid2">
            <section className="er-panel">
              <div className="er-panel__head">
                <h2 className="er-panel__title">{t.stayTitle}</h2>
              </div>
              <div className="er-panel__body">
                <dl className="er-kv">
                  <dt>{t.start}</dt>
                  <dd className="er-mono">{b.start}</dd>
                  <dt>{t.end}</dt>
                  <dd className="er-mono">{b.end}</dd>
                  <dt>{t.checkin}</dt>
                  <dd>{b.scheduledCheckinTime ?? '—'}</dd>
                  <dt>{t.guests}</dt>
                  <dd>{b.guests ?? '—'}</dd>
                  <dt>{t.effectiveTotal}</dt>
                  <dd dir="ltr">{b.effectiveTotal}</dd>
                  <dt>{t.initialTotal}</dt>
                  <dd dir="ltr">{b.initialTotal}</dd>
                  <dt>{t.deposit}</dt>
                  <dd dir="ltr">{b.agreedDeposit ?? '—'}</dd>
                  <dt>{t.createdAt}</dt>
                  <dd className="er-mono">{b.createdAt.slice(0, 10)}</dd>
                </dl>
              </div>
            </section>
            <section className="er-panel">
              <div className="er-panel__head">
                <h2 className="er-panel__title">{t.recentActivity}</h2>
                <span className="er-spacer" />
                <Link className="er-link" to={`?tab=history`} replace>
                  {t.viewHistory}
                </Link>
              </div>
              <div className="er-panel__body">
                <QueryFailure query={history} />
                {entries.length === 0 ? (
                  <p className="er-empty">{t.none}</p>
                ) : (
                  <ol className="er-timeline">
                    {[...entries]
                      .slice(-4)
                      .reverse()
                      .map((h) => (
                        <li key={`${h.recordedAt}-${h.kind}`} className="er-event">
                          <span className="er-event__ico er-event__ico--info">
                            <Icon name="clock" />
                          </span>
                          <span style={{ minWidth: 0 }}>
                            <span className="er-cell-main" style={{ display: 'block' }}>
                              {historyTitle(h, locale)}
                            </span>
                            <span className="er-cell-sub er-clip" style={{ display: 'block' }}>
                              {historySummary(h, locale, unitNumber)}
                            </span>
                          </span>
                          <span className="er-cell-sub">{riyadhDateTime(locale, h.recordedAt)}</span>
                        </li>
                      ))}
                  </ol>
                )}
              </div>
            </section>
          </div>
        )}
        {tab === 'payments' && <BookingFinancePanel bookingId={b.id} draft={b.status === 'DRAFT'} session={session} />}
        {tab === 'documents' && <BookingDocuments bookingId={b.id} session={session} />}
        {tab === 'contract' && <BookingContractPanel bookingId={b.id} session={session} />}
        {tab === 'history' && (
          <section className="er-panel">
            <div className="er-panel__head">
              <h2 className="er-panel__title">{t.history}</h2>
              <span className="er-cell-sub">{entries.length}</span>
            </div>
            <QueryFailure query={history} />
            <table className="er-ledger-table">
              <thead>
                <tr>
                  <th>{t.when}</th>
                  <th>{t.event}</th>
                  <th>{t.details}</th>
                  <th>{t.reason}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((h) => (
                  <tr key={`${h.recordedAt}-${h.kind}-${h.details.map((d) => d.value).join('|')}`}>
                    <td className="er-cell-sub" style={{ whiteSpace: 'nowrap' }}>
                      {riyadhDateTime(locale, h.recordedAt)}
                    </td>
                    <td className="er-cell-main">{historyTitle(h, locale)}</td>
                    <td>{historySummary(h, locale, unitNumber)}</td>
                    <td>{h.reason ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </main>
  );
}

type Dialog = 'terms' | 'dates' | 'move' | 'extend' | 'price' | 'cancel' | 'noShow' | 'early' | 'archive' | 'restore';

function BookingActions({
  booking,
  session,
  onChanged,
}: {
  readonly booking: Booking;
  readonly session: CurrentSession;
  readonly onChanged: () => void;
}) {
  const client = useApiClient();
  const t = useText(text);
  const action = useAction();
  const [v, setV] = useState({
    start: booking.start,
    end: booking.end,
    unitId: '',
    newEnd: '',
    added: '',
    newTotal: '',
    reason: '',
    guests: String(booking.guests ?? ''),
    deposit: booking.agreedDeposit ?? '',
    checkin: booking.scheduledCheckinTime ?? '',
    occupantId: booking.occupantId ?? '',
  });
  const set = (field: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [field]: e.target.value });
  const specific = {
    unit_unavailable: t.unavailable,
    physical_occupied: t.occupied,
    contract_not_signed: t.contractRequired,
    rent_not_confirmed: t.rentRequired,
    exit_before_end: t.exitBeforeEnd,
    exit_not_early: t.exitNotEarly,
    rent_not_settled: t.archiveRent,
    deposit_unsettled: t.archiveDeposit,
    finance_pending: t.archivePending,
    concurrent_change: t.stale,
    already_checked_in: t.alreadyCheckedIn,
    occurred_before_start: t.beforeStart,
    occurred_in_future: t.inFuture,
    status_not_allowed: t.statusNotAllowed,
  };
  const run = async (call: (key: string) => Promise<{ readonly status: number; readonly body: unknown }>) => {
    if (
      await action.run(call, specific, {
        onFailure: (failure) => {
          if (failure.reason === 'concurrent_change') onChanged();
        },
      })
    ) {
      onChanged();
    }
  };
  const path = { bookingId: booking.id };
  const draft = booking.status === 'DRAFT';
  const reserved = booking.status === 'RESERVED';
  const active = booking.status === 'ACTIVE';
  const inHouse = active && booking.stay && !booking.stay.checkedOutAt;
  const final = ['COMPLETED', 'CANCELLED', 'ENDED_EARLY', 'NO_SHOW'].includes(booking.status);
  const [open, setOpen] = useState<Dialog | null>(null);
  const show = (dialog: Dialog) => {
    action.setError(null);
    setV({ ...v, reason: '' });
    setOpen(dialog);
  };
  const close = () => setOpen(null);
  // A dialog closes once its command succeeds; a refusal stays visible in it.
  const submit = async (call: (key: string) => Promise<{ readonly status: number; readonly body: unknown }>) => {
    let ok = false;
    await run(async (key) => {
      const response = await call(key);
      ok = response.status < 400;
      return response;
    });
    if (ok) setOpen(null);
  };
  const reasonField = (required: boolean) => (
    <Field id="act-reason" label={required ? t.reason : t.reasonOptional}>
      <input id="act-reason" value={v.reason} onChange={set('reason')} />
    </Field>
  );
  const foot = (label: string, danger = false, disabled = false) => (
    <>
      <button className="er-btn er-btn--secondary" type="button" onClick={close}>
        {t.back}
      </button>
      <button
        className={danger ? 'er-btn er-btn--danger' : 'er-btn er-btn--primary'}
        type="submit"
        disabled={action.busy || disabled}
      >
        {label}
      </button>
    </>
  );
  const opener = (dialog: Dialog, label: string, icon: IconName, tone: 'secondary' | 'danger' = 'secondary') => (
    <button
      className={tone === 'danger' ? 'er-btn er-btn--danger-quiet' : 'er-btn er-btn--secondary'}
      type="button"
      onClick={() => show(dialog)}
    >
      <Icon name={icon} />
      {label}
    </button>
  );
  const sub = `${booking.displayReference} · ${booking.start} → ${booking.end}`;
  const canTerms = (draft || reserved) && has(session, 'booking.edit');
  return (
    <section className="er-whead__actions" aria-label={t.actions}>
      <div>
        {!open && action.error && <Alert kind="error">{action.error}</Alert>}
        <div className="er-actionbar" inert={open !== null} aria-hidden={open !== null ? true : undefined}>
          {draft && has(session, 'booking.create') && (
            <button
              className="er-btn er-btn--primary"
              type="button"
              disabled={action.busy}
              onClick={() =>
                run((key) => client.reserveBooking({ path, headers: { 'idempotency-key': key }, body: {} }))
              }
            >
              <Icon name="check" />
              {t.reserve}
            </button>
          )}
          {active && !booking.stay && has(session, 'booking.check_in') && (
            <button
              className="er-btn er-btn--primary"
              type="button"
              disabled={action.busy}
              onClick={() =>
                run((key) => client.checkInBooking({ path, headers: { 'idempotency-key': key }, body: {} }))
              }
            >
              <Icon name="login" className="er-flip" />
              {t.checkIn}
            </button>
          )}
          {inHouse && has(session, 'booking.check_out') && (
            <button
              className="er-btn er-btn--primary"
              type="button"
              disabled={action.busy}
              onClick={() =>
                run((key) => client.checkOutBooking({ path, headers: { 'idempotency-key': key }, body: {} }))
              }
            >
              <Icon name="logout" className="er-flip" />
              {t.checkOut}
            </button>
          )}
          {canTerms && opener('terms', reserved ? t.changeTerms : t.editDetails, 'pen')}
          {reserved && has(session, 'booking.date_change') && opener('dates', t.changeDates, 'calendar')}
          {(reserved || active) && has(session, 'booking.unit_move') && opener('move', t.move, 'building')}
          {(reserved || active) && has(session, 'booking.extension') && opener('extend', t.extend, 'plus')}
          {!draft && has(session, 'booking.price_change') && opener('price', t.price, 'wallet')}
          {final && has(session, 'booking.archive') && !booking.archived && opener('archive', t.archive, 'archive')}
          {booking.archived && has(session, 'booking.archive') && opener('restore', t.restore, 'undo')}
          {reserved && has(session, 'booking.no_show') && opener('noShow', t.noShow, 'x', 'danger')}
          {(draft || reserved) && has(session, 'booking.cancel') && opener('cancel', t.cancel, 'x', 'danger')}
          {inHouse && has(session, 'booking.early_exit') && opener('early', t.earlyExit, 'logout', 'danger')}
        </div>
      </div>
      {open === 'terms' && (
        <Modal
          title={reserved ? t.changeTerms : t.editDetails}
          sub={sub}
          onClose={close}
          wide
          onSubmit={() =>
            submit((key) => {
              const request = {
                path,
                headers: { 'idempotency-key': key },
                body: {
                  ...(v.guests ? { guests: Number(v.guests) } : {}),
                  agreedDeposit: v.deposit.trim() || null,
                  scheduledCheckinTime: v.checkin || null,
                  ...(v.occupantId ? { occupantId: v.occupantId } : {}),
                },
              };
              return draft
                ? client.editDraftBooking({
                    ...request,
                    body: { ...request.body, guests: v.guests ? Number(v.guests) : null },
                  })
                : client.changeUnsignedBookingTerms(request);
            })
          }
          footer={foot(t.apply)}
        >
          {action.error && <Alert kind="error">{action.error}</Alert>}
          <div className="er-formgrid">
            <Field id="edit-guests" label={t.guests}>
              <input id="edit-guests" type="number" min={1} value={v.guests} onChange={set('guests')} />
            </Field>
            <Field id="edit-deposit" label={t.deposit}>
              <input id="edit-deposit" dir="ltr" inputMode="decimal" value={v.deposit} onChange={set('deposit')} />
            </Field>
            <Field id="edit-checkin" label={t.checkin}>
              <input id="edit-checkin" type="time" value={v.checkin} onChange={set('checkin')} />
            </Field>
            {(booking.reserveGaps.includes('company_occupant') || booking.occupantId) && (
              <CompanyOccupantPicker
                id="edit-occupant"
                customerId={booking.customerId}
                value={v.occupantId}
                onChange={(occupantId) => setV({ ...v, occupantId })}
              />
            )}
          </div>
        </Modal>
      )}
      {open === 'dates' && (
        <Modal
          title={t.changeDates}
          sub={sub}
          onClose={close}
          onSubmit={() =>
            submit((key) =>
              client.changeBookingDates({
                path,
                headers: { 'idempotency-key': key },
                body: { startDate: v.start, endDate: v.end, ...(v.reason.trim() ? { reason: v.reason.trim() } : {}) },
              }),
            )
          }
          footer={foot(t.apply)}
        >
          {action.error && <Alert kind="error">{action.error}</Alert>}
          <div className="er-formgrid">
            <Field id="dc-start" label={t.start}>
              <input id="dc-start" type="date" value={v.start} onChange={set('start')} />
            </Field>
            <Field id="dc-end" label={t.end}>
              <input id="dc-end" type="date" value={v.end} onChange={set('end')} />
            </Field>
          </div>
          {reasonField(false)}
        </Modal>
      )}
      {open === 'move' && (
        <Modal
          title={t.move}
          sub={sub}
          onClose={close}
          onSubmit={() =>
            submit((key) =>
              client.moveBookingUnit({
                path,
                headers: { 'idempotency-key': key },
                body: { unitId: v.unitId.trim(), ...(v.reason.trim() ? { reason: v.reason.trim() } : {}) },
              }),
            )
          }
          footer={foot(t.apply)}
        >
          {action.error && <Alert kind="error">{action.error}</Alert>}
          <Field id="mv-unit" label={t.toUnit}>
            <MoveUnitPicker booking={booking} value={v.unitId} onChange={(unitId) => setV({ ...v, unitId })} />
          </Field>
          {reasonField(false)}
        </Modal>
      )}
      {open === 'extend' && (
        <Modal
          title={t.extend}
          sub={sub}
          onClose={close}
          onSubmit={() =>
            submit((key) =>
              client.extendBooking({
                path,
                headers: { 'idempotency-key': key },
                body: {
                  newEndDate: v.newEnd,
                  addedAmount: v.added.trim(),
                  ...(v.reason.trim() ? { reason: v.reason.trim() } : {}),
                },
              }),
            )
          }
          footer={foot(t.apply)}
        >
          {action.error && <Alert kind="error">{action.error}</Alert>}
          <div className="er-formgrid">
            <Field id="ex-end" label={t.newEnd}>
              <input id="ex-end" type="date" value={v.newEnd} onChange={set('newEnd')} />
            </Field>
            <Field id="ex-added" label={t.added}>
              <input id="ex-added" dir="ltr" inputMode="decimal" value={v.added} onChange={set('added')} />
            </Field>
          </div>
          <p className="er-hint">{`${t.effectiveTotal}: ${booking.effectiveTotal}`}</p>
          {reasonField(false)}
        </Modal>
      )}
      {open === 'price' && (
        <Modal
          title={t.price}
          sub={sub}
          onClose={close}
          onSubmit={() =>
            submit((key) =>
              client.changeBookingPrice({
                path,
                headers: { 'idempotency-key': key },
                body: { newTotal: v.newTotal.trim(), reason: v.reason.trim() },
              }),
            )
          }
          footer={foot(t.apply, false, !v.reason.trim())}
        >
          {action.error && <Alert kind="error">{action.error}</Alert>}
          <Field id="pc-total" label={t.newTotal}>
            <input id="pc-total" dir="ltr" inputMode="decimal" value={v.newTotal} onChange={set('newTotal')} />
          </Field>
          <p className="er-hint">{`${t.effectiveTotal}: ${booking.effectiveTotal}`}</p>
          {reasonField(true)}
        </Modal>
      )}
      {(open === 'cancel' || open === 'noShow' || open === 'early') && (
        <Modal
          title={open === 'cancel' ? t.cancel : open === 'noShow' ? t.noShow : t.earlyExit}
          sub={sub}
          onClose={close}
          onSubmit={() =>
            submit((key) => {
              const request = { path, headers: { 'idempotency-key': key }, body: { reason: v.reason.trim() } };
              return open === 'cancel'
                ? client.cancelBooking(request)
                : open === 'noShow'
                  ? client.markBookingNoShow(request)
                  : client.recordEarlyExit(request);
            })
          }
          footer={foot(
            open === 'cancel' ? t.cancel : open === 'noShow' ? t.noShow : t.earlyExit,
            true,
            !v.reason.trim(),
          )}
        >
          {action.error && <Alert kind="error">{action.error}</Alert>}
          <div className="er-callout er-callout--warn">
            <Icon name="alert" />
            <span>{t.recordedKept}</span>
          </div>
          {reasonField(true)}
        </Modal>
      )}
      {(open === 'archive' || open === 'restore') && (
        <Modal
          title={open === 'archive' ? t.archive : t.restore}
          sub={sub}
          onClose={close}
          onSubmit={() =>
            submit((key) => {
              const request = {
                path,
                headers: { 'idempotency-key': key },
                body: { ...(v.reason.trim() ? { reason: v.reason.trim() } : {}) },
              };
              return open === 'archive' ? client.archiveBooking(request) : client.restoreBooking(request);
            })
          }
          footer={foot(open === 'archive' ? t.archive : t.restore)}
        >
          {action.error && <Alert kind="error">{action.error}</Alert>}
          {reasonField(false)}
        </Modal>
      )}
    </section>
  );
}

function MoveUnitPicker({
  booking,
  value,
  onChange,
}: {
  readonly booking: Booking;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  const client = useApiClient();
  const t = useText(text);
  const now = new Date();
  const today = businessDate(now);
  const start = booking.status === 'ACTIVE' && today > booking.start ? today : booking.start;
  const end = booking.end > start ? booking.end : businessDate(now, 1);
  const query = { branchId: booking.branchId, start, end };
  const units = useQuery({
    queryKey: ['availability', 'move', booking.id, booking.unitId, query],
    queryFn: async ({ signal }) => {
      const result = await client.getUnitAvailability({ query }, { signal });
      if (result.status !== 200) throw new Error('move_availability_unavailable');
      return result.body.units;
    },
  });
  return (
    <>
      {units.isError && <Alert kind="error">{t.availabilityFailed}</Alert>}
      <select id="mv-unit" value={value} onChange={(event) => onChange(event.target.value)} disabled={!units.isSuccess}>
        <option value="">—</option>
        {(units.data ?? [])
          .filter((unit) => unit.unitId !== booking.unitId)
          .map((unit) => (
            <option key={unit.unitId} value={unit.unitId} disabled={!unit.available || unit.physicalOccupied}>
              {unit.unitNumber}
              {unit.physicalOccupied ? ` · ${t.physicalOccupancy}` : !unit.available ? ` · ${t.unavailable}` : ''}
            </option>
          ))}
      </select>
    </>
  );
}

// ---- Maintenance --------------------------------------------------------------------------------

export function MaintenancePage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const ar = useLocale().locale === 'ar';
  const queries = useQueryClient();
  const action = useAction();
  const list = useQuery({
    queryKey: ['maintenance'],
    queryFn: ({ signal }) => loadMaintenance(client, signal),
  });
  const [form, setForm] = useState({ unitId: '', start: '', expectedEnd: '', reason: '', notes: '' });
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [plan, setPlan] = useState({ expectedEnd: '', reason: '' });
  const detail = useQuery({
    queryKey: ['maintenance', selected],
    enabled: Boolean(selected),
    queryFn: async ({ signal }) => {
      if (!selected) return null;
      const r = await client.getMaintenance({ path: { maintenanceId: selected } }, { signal });
      return readResponse(r);
    },
  });
  const refresh = async () => {
    // An initial read may have taken its snapshot before this command.
    // Cancel it before refetching, even when it has no cached data yet.
    await queries.cancelQueries({ queryKey: ['maintenance'] });
    await queries.invalidateQueries({ queryKey: ['maintenance'] });
  };
  const run = async (call: (key: string) => Promise<{ readonly status: number; readonly body: unknown }>) => {
    if (await action.run(call, { unit_has_conflicting_allocation: t.unitConflict })) await refresh();
  };
  const phaseLabel = (phase: Maintenance['phase']) =>
    t[`phase_${phase}` as 'phase_planned' | 'phase_open' | 'phase_overdue' | 'phase_closed' | 'phase_cancelled'];

  return (
    <main>
      <h1>{t.maintenance}</h1>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      <section className="er-card">
        {list.isPending ? (
          <Loading />
        ) : list.isError ? (
          <Alert kind="error">{ar ? 'تعذر تحميل الصيانة.' : 'Maintenance could not be loaded.'}</Alert>
        ) : (list.data ?? []).length === 0 ? (
          <p>{t.noMaintenance}</p>
        ) : (
          <table className="er-table">
            <thead>
              <tr>
                <th>{t.unit}</th>
                <th>{t.start}</th>
                <th>{t.expectedEnd}</th>
                <th>{t.phase}</th>
                <th>{t.reason}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((m) => (
                <tr key={m.id}>
                  <td className="er-mono">{m.unitNumber}</td>
                  <td className="er-mono">{m.startDate}</td>
                  <td className="er-mono">{m.expectedEndDate ?? '—'}</td>
                  <td>
                    <Badge
                      tone={
                        m.phase === 'overdue'
                          ? 'danger'
                          : m.phase === 'closed' || m.phase === 'cancelled'
                            ? 'off'
                            : m.phase === 'planned'
                              ? 'warn'
                              : 'ok'
                      }
                    >
                      {phaseLabel(m.phase)}
                    </Badge>
                  </td>
                  <td>{m.reason}</td>
                  <td>
                    {m.status === 'OPEN' && m.phase !== 'planned' && has(session, 'maintenance.close') && (
                      <button
                        className="er-button secondary"
                        type="button"
                        onClick={() =>
                          window.confirm(t.readiness) &&
                          run((key) =>
                            client.closeMaintenance({
                              path: { maintenanceId: m.id },
                              headers: { 'idempotency-key': key },
                              body: { readinessConfirmed: true },
                            }),
                          )
                        }
                      >
                        {t.close}
                      </button>
                    )}
                    <button className="er-button secondary" type="button" onClick={() => setSelected(m.id)}>
                      {t.planningHistory}
                    </button>
                    {m.status === 'OPEN' && m.phase === 'planned' && has(session, 'maintenance.cancel_planned') && (
                      <button
                        className="er-button danger"
                        type="button"
                        disabled={!reason.trim()}
                        onClick={() =>
                          run((key) =>
                            client.cancelPlannedMaintenance({
                              path: { maintenanceId: m.id },
                              headers: { 'idempotency-key': key },
                              body: { reason: reason.trim() },
                            }),
                          )
                        }
                      >
                        {t.cancelPlanned}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Field id="mt-action-reason" label={t.reason}>
          <input id="mt-action-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <QueryFailure query={detail} />
        {!detail.isError && detail.data && (
          <div>
            <MaintenanceDocuments maintenanceId={detail.data.maintenance.id} session={session} />
            <h2>{t.planningHistory}</h2>
            {(detail.data.planning ?? []).length === 0 ? (
              <p>{t.noMaintenance}</p>
            ) : (
              <ul>
                {detail.data.planning.map((entry) => (
                  <li key={entry.recordedAt}>
                    <span className="er-mono">
                      {entry.previousExpectedEndDate ?? '—'} → {entry.newExpectedEndDate ?? '—'}
                    </span>{' '}
                    {entry.reason}
                  </li>
                ))}
              </ul>
            )}
            {detail.data.maintenance.status === 'OPEN' && has(session, 'maintenance.planning') && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!selected) return;
                  void run((key) =>
                    client.changeMaintenancePlanning({
                      path: { maintenanceId: selected },
                      headers: { 'idempotency-key': key },
                      body: {
                        expectedEndDate: plan.expectedEnd || null,
                        reason: plan.reason.trim(),
                      },
                    }),
                  );
                }}
                noValidate
              >
                <h3>{t.planning}</h3>
                <Field id="mt-plan-end" label={t.expectedEnd}>
                  <input
                    id="mt-plan-end"
                    type="date"
                    value={plan.expectedEnd}
                    onChange={(event) => setPlan({ ...plan, expectedEnd: event.target.value })}
                  />
                </Field>
                <Field id="mt-plan-reason" label={t.reason}>
                  <input
                    id="mt-plan-reason"
                    value={plan.reason}
                    onChange={(event) => setPlan({ ...plan, reason: event.target.value })}
                  />
                </Field>
                <button className="er-button secondary" type="submit" disabled={action.busy || !plan.reason.trim()}>
                  {t.savePlanning}
                </button>
              </form>
            )}
          </div>
        )}
      </section>
      {has(session, 'maintenance.create_edit') && (
        <section className="er-card">
          <h2>{t.newMaintenance}</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run((key) =>
                client.createMaintenance({
                  headers: { 'idempotency-key': key },
                  body: {
                    unitId: form.unitId.trim(),
                    startDate: form.start,
                    reason: form.reason.trim(),
                    ...(form.expectedEnd ? { expectedEndDate: form.expectedEnd } : {}),
                    ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
                  },
                }),
              );
            }}
            noValidate
          >
            <Field id="mt-unit" label={t.toUnit}>
              <input
                id="mt-unit"
                dir="ltr"
                value={form.unitId}
                onChange={(e) => setForm({ ...form, unitId: e.target.value })}
              />
            </Field>
            <Field id="mt-start" label={t.start}>
              <input
                id="mt-start"
                type="date"
                value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
              />
            </Field>
            <Field id="mt-expected" label={t.expectedEnd}>
              <input
                id="mt-expected"
                type="date"
                value={form.expectedEnd}
                onChange={(e) => setForm({ ...form, expectedEnd: e.target.value })}
              />
            </Field>
            <Field id="mt-reason" label={t.reason}>
              <input
                id="mt-reason"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
            </Field>
            <Field id="mt-notes" label={t.notes}>
              <input id="mt-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <button
              className="er-button"
              type="submit"
              disabled={action.busy || !form.unitId || !form.start || !form.reason.trim()}
            >
              {t.newMaintenance}
            </button>
          </form>
        </section>
      )}
    </main>
  );
}
